/** Suntronix ONE — Service 360 (Service Detail).
 *  Route: /services/detail/:code
 *  Shows: description, what's included/excluded, prerequisites, required info,
 *         SLA, price hint, related add-ons, dependencies, and "Start request".
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ServiceTestPanel from "@/components/app/ServiceTestPanel";
import {
  ArrowLeft, Sparkles, Star, Clock, ShieldCheck, CheckCircle2, XCircle,
  Info, Zap, Layers, ChevronRight, Beaker,
} from "lucide-react";

function formatPricing(s) {
  const v = s.pricing_visibility || "show_price";
  const map = {
    quotation_required: "Quotation on request",
    contact_suntronix: "Contact Suntronix",
    included_in_contract: "Included in your contract",
    show_estimate: "Estimate available",
    hide: null,
  };
  if (map[v] !== undefined && v !== "show_price") return map[v];
  if (s.default_price == null) return null;
  const cur = s.default_currency || "INR";
  try {
    const money = new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(s.default_price);
    return `${money} · ${(s.billing_method || s.pricing_type || "per_device").replace(/_/g, " ")}`;
  } catch { return `${cur} ${s.default_price}`; }
}

function List({ items, empty, testId }) {
  if (!items || !items.length) return empty ? <div className="text-sm text-slate-500" data-testid={testId}>{empty}</div> : null;
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {items.map((t, i) => (
        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 mt-1 text-emerald-600 shrink-0" strokeWidth={2} />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function ListNegative({ items }) {
  if (!items || !items.length) return null;
  return (
    <ul className="space-y-1.5">
      {items.map((t, i) => (
        <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
          <XCircle className="h-3.5 w-3.5 mt-1 text-rose-500 shrink-0" strokeWidth={2} />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ServiceDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuntronix = !!user && (!user.master_company_id || user.role === "super_admin" || String(user.role || "").startsWith("ops_") || String(user.role || "").startsWith("suntronix_"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    setLoading(true);
    api.get(`/services/${code}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Service not found"))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) return <div className="max-w-4xl space-y-4"><div className="st-skeleton h-8 w-64" /><div className="st-skeleton h-32" /><div className="st-skeleton h-40" /></div>;
  if (error) return (
    <div className="max-w-3xl space-y-4">
      <Link to="/services" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"><ArrowLeft className="h-3.5 w-3.5" /> Back to services</Link>
      <div className="bg-white border border-slate-200 rounded-md p-6 text-sm text-slate-700" data-testid="svc-detail-error">{error}</div>
    </div>
  );

  const s = data.service;
  const price = formatPricing(s);

  return (
    <div className="max-w-5xl space-y-6">
      <Link to={`/services/group/${s.group}`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="svc-detail-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </Link>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">{s.group.replace(/_/g, " ")}</div>
              {s.is_premium && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-[color:var(--st-accent)] bg-[color:var(--st-accent-soft)] rounded-full px-2 py-0.5">
                  <Sparkles className="h-3 w-3" /> Premium
                </span>
              )}
              {s.is_featured && !s.is_premium && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-700 bg-slate-100 rounded-full px-2 py-0.5">
                  <Star className="h-3 w-3" /> Featured
                </span>
              )}
            </div>
            <h1 className="mt-1 font-heading text-[26px] font-semibold text-slate-900 tracking-tight">
              {s.customer_facing_name || s.name}
            </h1>
            {s.short_description && (
              <p className="mt-2 text-[15px] text-slate-600 leading-relaxed max-w-2xl">{s.short_description}</p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-slate-600">
              {s.sla_target && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" strokeWidth={1.6} /> SLA {s.sla_target}</span>}
              {s.approval_required && <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.6} /> Approval required</span>}
              {price && <span className="font-mono text-slate-800">{price}</span>}
              {s.delivery_model && s.delivery_model !== "one_time" && (
                <span className="inline-flex items-center gap-1 capitalize"><Layers className="h-3.5 w-3.5" strokeWidth={1.6} /> {s.delivery_model.replace(/_/g, " ")}</span>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate(`/services/request/${s.code}`)}
            className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 inline-flex items-center gap-2 shrink-0"
            data-testid="svc-detail-start"
          >
            <Zap className="h-4 w-4" strokeWidth={1.6} /> Start request
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 border-b border-slate-200" data-testid="svc-tabs">
        {[
          { key: "overview", label: "Overview" },
          ...(data.variants?.length ? [{ key: "variants", label: `Variants (${data.variants.length})` }] : []),
          ...(isSuntronix ? [{ key: "productise", label: "Productise · Test" }] : []),
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              "text-xs font-medium h-9 px-3 -mb-px border-b-2 " +
              (tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900")
            }
            data-testid={`svc-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "variants" && data.variants?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md" data-testid="svc-variants-tab">
          <div className="px-5 py-3 border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">Service variants</div>
          <ul className="divide-y divide-slate-100">
            {data.variants.map((v) => (
              <li key={v.code} className="px-5 py-4" data-testid={`svc-variant-${v.code}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{v.customer_facing_name || v.name}</div>
                    {v.description && <div className="text-xs text-slate-600 mt-0.5">{v.description}</div>}
                    {v.difference_from_standard && <div className="text-[11px] text-slate-500 mt-1 italic">{v.difference_from_standard}</div>}
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                      {v.sla_target && <span>SLA {v.sla_target}</span>}
                      {v.default_price != null && <span className="font-mono text-slate-700">{v.default_currency || "INR"} {v.default_price} · {v.billing_method || "per_pickup"}</span>}
                      {v.pricing_modifier && v.pricing_modifier !== 1 && <span>Modifier ×{v.pricing_modifier}</span>}
                    </div>
                  </div>
                  <span className="text-[10px] font-mono text-slate-400 shrink-0">{v.code}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "productise" && isSuntronix && <ServiceTestPanel code={code} />}

      {tab === "overview" && (
      <>
      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {(s.customer_description && s.customer_description !== s.short_description) && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-2">About this service</div>
              <p className="text-sm text-slate-700 leading-relaxed">{s.customer_description}</p>
            </div>
          )}
          {s.what_is_included?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-3">What's included</div>
              <List items={s.what_is_included} testId="svc-includes" />
            </div>
          )}
          {s.what_is_excluded?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-3">What's not included</div>
              <ListNegative items={s.what_is_excluded} />
            </div>
          )}
          {s.required_information?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-3">Information we'll need</div>
              <List items={s.required_information} testId="svc-required-info" />
            </div>
          )}
          {s.prerequisites?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-3">Prerequisites</div>
              <List items={s.prerequisites} />
            </div>
          )}
          {data.dependencies?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-3">Related services</div>
              <ul className="divide-y divide-slate-100">
                {data.dependencies.map((d) => (
                  <li key={d.code} className="py-2">
                    <Link to={`/services/detail/${d.requires_service_code}`} className="flex items-center justify-between text-sm text-slate-700 hover:text-slate-900" data-testid={`svc-dep-${d.code}`}>
                      <span className="flex items-center gap-2"><Info className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.6} /> Requires <span className="font-medium">{d.requires_service_code}</span></span>
                      <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-3">At a glance</div>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Service code</dt><dd className="font-mono text-xs text-slate-800">{s.code}</dd></div>
              {s.category && <div className="flex justify-between gap-3"><dt className="text-slate-500">Category</dt><dd className="text-slate-800">{s.category}</dd></div>}
              {s.uom && <div className="flex justify-between gap-3"><dt className="text-slate-500">Unit of measure</dt><dd className="text-slate-800 capitalize">{s.uom.replace(/_/g, " ")}</dd></div>}
              {s.version && <div className="flex justify-between gap-3"><dt className="text-slate-500">Version</dt><dd className="text-slate-800">v{s.version}</dd></div>}
              {s.countries?.length > 0 && <div className="flex justify-between gap-3"><dt className="text-slate-500">Countries</dt><dd className="text-slate-800 text-right">{s.countries.join(", ")}</dd></div>}
            </dl>
          </div>

          {data.addons?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-3">Optional add-ons</div>
              <ul className="space-y-2">
                {data.addons.map((a) => (
                  <li key={a.code} className="text-sm" data-testid={`svc-addon-${a.code}`}>
                    <div className="font-medium text-slate-800">{a.name}</div>
                    {a.description && <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{a.description}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
      </>
      )}
    </div>
  );
}
